import React, { useEffect, useState } from "react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/solid";

const GitLogo = require("../assets/logos/GitLogo.png");
const LinkedinLogo = require("../assets/logos/LinkedinLogo.png");
const MailLogo = require("../assets/logos/MailLogo.png");

const JSIcon = require("../assets/icons/JSIcon.png");
const TSIcon = require("../assets/icons/TSIcon.png");

// VS Code style activity bar icons
const ActivityIcon = ({ icon, active, onClick }: { icon: string; active?: boolean; onClick?: () => void }) => (
  <button
    onClick={onClick}
    className={`w-12 h-12 flex items-center justify-center hover:text-white transition-colors ${active ? "text-white border-l-2 border-white bg-[#37373d]" : "text-[#858585]"
      }`}
  >
    <span className="text-xl">{icon}</span>
  </button>
);

const SideBar = ({
  setWidth,
  width,
  activeTab,
  setActiveTab,
}: {
  setWidth: React.Dispatch<React.SetStateAction<number>>;
  width: number;
  activeTab: string;
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const [isFolderOpen, setIsFolderOpen] = useState(true);
  const [isSrcOpen, setIsSrcOpen] = useState(true);

  const startResizing = (mouseDownEvent: React.MouseEvent) => {
    const handleMouseMove = (mouseMoveEvent: MouseEvent) => {
      const delta = mouseMoveEvent.clientX - mouseDownEvent.clientX;
      const newWidth = Math.max(170, mouseDownEvent.clientX + delta);
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  useEffect(() => {
    const savedWidth = localStorage.getItem("sideBarWidth");
    if (savedWidth) {
      setWidth(parseInt(savedWidth));
    }
  }, [setWidth]);

  const files = [
    { name: "Home.tsx", tab: "home", icon: JSIcon },
    { name: "About.tsx", tab: "about", icon: TSIcon },
    { name: "Resume.tsx", tab: "resume", icon: TSIcon },
    { name: "Projects.tsx", tab: "projects", icon: TSIcon },
    { name: "Contact.tsx", tab: "contact", icon: TSIcon },
  ];

  return (
    <div className="flex h-full">
      {/* Activity Bar */}
      <div className="w-12 bg-[#333333] flex flex-col items-center border-r border-[#252526]">
        <ActivityIcon icon="📂" active={true} />
        <ActivityIcon icon="🔍" />
        <ActivityIcon icon="⚙️" />

        {/* Social links at bottom of activity bar */}
        <div className="mt-auto mb-4 flex flex-col gap-3">
          <a
            href="https://github.com/mansha71"
            target="_blank"
            rel="noopener noreferrer"
            className="w-8 h-8 flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity duration-300 hover:scale-110"
          >
            <img src={GitLogo} alt="GitHub" className="w-6 h-6" />
          </a>
          <a
            href="https://www.linkedin.com/in/manshr/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-8 h-8 flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity duration-300 hover:scale-110"
          >
            <img src={LinkedinLogo} alt="LinkedIn" className="w-6 h-6" />
          </a>
          <a
            href="mailto:manan.sharma.dev@gmail.com"
            target="_blank"
            rel="noopener noreferrer"
            className="w-8 h-8 flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity duration-300 hover:scale-110"
          >
            <img src={MailLogo} alt="Email" className="w-6 h-6" />
          </a>
        </div>
      </div>

      {/* Explorer Panel */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Explorer Header */}
        <div className="h-9 flex items-center px-4 text-[11px] font-medium tracking-wider text-[#bbbbbb] uppercase bg-[#252526]">
          Explorer
        </div>

        {/* Workspace Section */}
        <div className="flex-1 overflow-auto">
          {/* Workspace Header */}
          <button
            onClick={() => setIsFolderOpen(!isFolderOpen)}
            className="w-full h-6 flex items-center px-2 text-[11px] font-semibold tracking-wide text-[#cccccc] uppercase bg-[#383838] hover:bg-[#404040] transition-colors"
          >
            {isFolderOpen ? (
              <ChevronDownIcon className="w-4 h-4 mr-1" />
            ) : (
              <ChevronRightIcon className="w-4 h-4 mr-1" />
            )}
            Portfolio
          </button>

          {/* Folder Contents */}
          {isFolderOpen && (
            <div className="py-1">
              {/* Folder Row - Now clickable */}
              <button
                onClick={() => setIsSrcOpen(!isSrcOpen)}
                className="w-full flex items-center px-4 py-1 text-[#cccccc] text-sm hover:bg-[#2a2d2e] transition-colors"
              >
                {isSrcOpen ? (
                  <ChevronDownIcon className="w-4 h-4 mr-1 text-[#cccccc]" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4 mr-1 text-[#cccccc]" />
                )}
                <span className="text-[#dcb67a] mr-2">📁</span>
                <span className="text-[#cccccc]">src</span>
              </button>

              {/* File List - Conditionally rendered */}
              {isSrcOpen && (
                <div className="ml-4">
                  {files.map((file) => (
                    <button
                      key={file.tab}
                      type="button"
                      onClick={() => setActiveTab(file.tab)}
                      className={`w-full flex items-center px-4 py-[3px] text-sm transition-colors ${activeTab === file.tab
                        ? "bg-[#094771] text-white"
                        : "text-[#cccccc] hover:bg-[#2a2d2e]"
                        }`}
                    >
                      <img src={file.icon} alt="" className="w-4 h-4 mr-2" />
                      <span className="truncate">{file.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Resize Handle */}
      <div
        onMouseDown={startResizing}
        className="w-1 bg-[#252526] hover:bg-[#007acc] cursor-col-resize transition-colors"
      />
    </div>
  );
};

export default SideBar;
